// src/services/mediaAlbumServices.js
import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/media-albums`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const buildQuery = (params = {}) => {
  const q = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (typeof value === "boolean") {
      q.append(key, value ? "true" : "false");
      return;
    }

    if (Array.isArray(value)) {
      const joined = value
        .map((v) => String(v).trim())
        .filter(Boolean)
        .join(",");

      if (joined) q.append(key, joined);
      return;
    }

    const s = String(value).trim();
    if (s !== "") q.append(key, s);
  });

  const qs = q.toString();
  return qs ? `?${qs}` : "";
};

export const toAbsolutePublicUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const cleanPath = String(url).startsWith("/") ? url : `/${url}`;

  return `${base}${cleanPath}`;
};

export const isImageMedia = (media) => {
  const type = String(media?.type || "").toLowerCase();
  const mime = String(media?.mime_type || "").toLowerCase();
  const url = String(media?.url || "").toLowerCase();

  return (
    type === "image" ||
    mime.startsWith("image/") ||
    /\.(jpe?g|png|webp|gif)$/i.test(url)
  );
};

export const getAlbumCoverMedia = (album) => {
  if (!album) return null;

  if (album.cover_media) return album.cover_media;

  const items = Array.isArray(album.items) ? album.items : [];
  const firstItemWithMedia = items.find((item) => item?.media);

  return firstItemWithMedia?.media || null;
};

export const getAlbumCoverUrl = (album) => {
  const coverMedia = getAlbumCoverMedia(album);
  return toAbsolutePublicUrl(coverMedia?.url);
};

export const normalizeAlbumItem = (item = {}) => {
  const media = item.media || null;

  return {
    ...item,
    id: item.id,
    album_id: item.album_id,
    media_id: item.media_id,
    display_order: toNumber(item.display_order, 0),
    caption: item.caption || "",
    alt_text_override: item.alt_text_override || "",
    is_visible: Boolean(item.is_visible),
    media,
    image_url: toAbsolutePublicUrl(media?.url),
    title: item.caption || media?.title || media?.original_name || media?.file_name || "",
    alt_text:
      item.alt_text_override ||
      media?.alt_text ||
      item.caption ||
      media?.title ||
      "Photo de l'album",
  };
};

export const normalizeAlbum = (album = {}) => {
  const items = Array.isArray(album.items)
    ? album.items.map(normalizeAlbumItem)
    : [];

  return {
    ...album,
    id: album.id,
    title: album.title || "",
    slug: album.slug || "",
    description: album.description || "",
    cover_media_id: album.cover_media_id || null,
    cover_media: album.cover_media || null,
    is_active: Boolean(album.is_active),
    is_public: Boolean(album.is_public),
    show_on_home: Boolean(album.show_on_home),
    display_order: toNumber(album.display_order, 0),
    total_items: toNumber(album.total_items, items.length),
    items,
    cover_url: getAlbumCoverUrl({
      ...album,
      items,
    }),
  };
};

export const searchAlbums = async (params = {}) => {
  try {
    const qs = buildQuery(params);

    const { data } = await axios.get(`${apiUrl}${qs}`, {
      headers: authHeaders(),
    });

    return {
      items: Array.isArray(data?.items)
        ? data.items.map(normalizeAlbum)
        : [],
      total: toNumber(data?.total, 0),
      page: toNumber(data?.page, 1),
      limit: toNumber(data?.limit, 20),
      totalPages: toNumber(data?.totalPages, 1),
    };
  } catch (e) {
    console.error("Erreur searchAlbums:", e);

    return {
      items: [],
      total: 0,
      page: 1,
      limit: 20,
      totalPages: 1,
    };
  }
};

export const getAlbumById = async (id) => {
  if (!id) throw new Error("getAlbumById: id manquant.");

  const { data } = await axios.get(`${apiUrl}/${id}`, {
    headers: authHeaders(),
  });

  return normalizeAlbum(data);
};

export const getPublicAlbumBySlug = async (slug) => {
  if (!slug) throw new Error("getPublicAlbumBySlug: slug manquant.");

  const { data } = await axios.get(`${apiUrl}/public/${slug}`);

  return normalizeAlbum(data);
};

export const getHomeCarouselAlbum = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/home-carousel`);

    if (!data || data.album === null) {
      return null;
    }

    return normalizeAlbum(data);
  } catch (e) {
    console.error("Erreur getHomeCarouselAlbum:", e);
    return null;
  }
};

export const createAlbum = async (payload = {}) => {
  const { data } = await axios.post(`${apiUrl}`, payload, {
    headers: {
      ...authHeaders(),
      "Content-Type": "application/json",
    },
  });

  return normalizeAlbum(data);
};

export const updateAlbum = async (id, payload = {}) => {
  if (!id) throw new Error("updateAlbum: id manquant.");

  const { data } = await axios.put(`${apiUrl}/${id}`, payload, {
    headers: {
      ...authHeaders(),
      "Content-Type": "application/json",
    },
  });

  return normalizeAlbum(data);
};

export const deleteAlbum = async (id) => {
  if (!id) throw new Error("deleteAlbum: id manquant.");

  const { data } = await axios.delete(`${apiUrl}/${id}`, {
    headers: authHeaders(),
  });

  return data;
};

export const addItemsToAlbum = async (albumId, mediaIds = []) => {
  if (!albumId) throw new Error("addItemsToAlbum: albumId manquant.");

  const ids = Array.isArray(mediaIds)
    ? mediaIds.map(Number).filter((id) => Number.isInteger(id) && id > 0)
    : [];

  if (ids.length === 0) {
    throw new Error("addItemsToAlbum: aucune photo sélectionnée.");
  }

  const { data } = await axios.post(
    `${apiUrl}/${albumId}/items`,
    {
      media_ids: ids,
    },
    {
      headers: {
        ...authHeaders(),
        "Content-Type": "application/json",
      },
    }
  );

  return normalizeAlbum(data);
};

export const updateAlbumItem = async (albumId, itemId, payload = {}) => {
  if (!albumId) throw new Error("updateAlbumItem: albumId manquant.");
  if (!itemId) throw new Error("updateAlbumItem: itemId manquant.");

  const { data } = await axios.put(
    `${apiUrl}/${albumId}/items/${itemId}`,
    payload,
    {
      headers: {
        ...authHeaders(),
        "Content-Type": "application/json",
      },
    }
  );

  return normalizeAlbum(data);
};

export const removeAlbumItem = async (albumId, itemId) => {
  if (!albumId) throw new Error("removeAlbumItem: albumId manquant.");
  if (!itemId) throw new Error("removeAlbumItem: itemId manquant.");

  const { data } = await axios.delete(`${apiUrl}/${albumId}/items/${itemId}`, {
    headers: authHeaders(),
  });

  return normalizeAlbum(data);
};

export const reorderAlbumItems = async (albumId, orderedItemIds = []) => {
  if (!albumId) throw new Error("reorderAlbumItems: albumId manquant.");

  const itemIds = Array.isArray(orderedItemIds)
    ? orderedItemIds.map(Number).filter((id) => Number.isInteger(id) && id > 0)
    : [];

  if (itemIds.length === 0) {
    throw new Error("reorderAlbumItems: aucun ordre reçu.");
  }

  const { data } = await axios.put(
    `${apiUrl}/${albumId}/items/reorder`,
    {
      item_ids: itemIds,
    },
    {
      headers: {
        ...authHeaders(),
        "Content-Type": "application/json",
      },
    }
  );

  return normalizeAlbum(data);
};

export const reorderAlbumItemsByMediaIds = async (
  albumId,
  orderedMediaIds = []
) => {
  if (!albumId) {
    throw new Error("reorderAlbumItemsByMediaIds: albumId manquant.");
  }

  const mediaIds = Array.isArray(orderedMediaIds)
    ? orderedMediaIds.map(Number).filter((id) => Number.isInteger(id) && id > 0)
    : [];

  if (mediaIds.length === 0) {
    throw new Error("reorderAlbumItemsByMediaIds: aucun ordre reçu.");
  }

  const { data } = await axios.put(
    `${apiUrl}/${albumId}/items/reorder`,
    {
      media_ids: mediaIds,
    },
    {
      headers: {
        ...authHeaders(),
        "Content-Type": "application/json",
      },
    }
  );

  return normalizeAlbum(data);
};

export const setAlbumCover = async (albumId, mediaId) => {
  if (!albumId) throw new Error("setAlbumCover: albumId manquant.");
  if (!mediaId) throw new Error("setAlbumCover: mediaId manquant.");

  const { data } = await axios.patch(
    `${apiUrl}/${albumId}/cover`,
    {
      media_id: mediaId,
    },
    {
      headers: {
        ...authHeaders(),
        "Content-Type": "application/json",
      },
    }
  );

  return normalizeAlbum(data);
};

export const toggleAlbumActive = async (album) => {
  if (!album?.id) throw new Error("toggleAlbumActive: album manquant.");

  return updateAlbum(album.id, {
    is_active: !album.is_active,
  });
};

export const toggleAlbumPublic = async (album) => {
  if (!album?.id) throw new Error("toggleAlbumPublic: album manquant.");

  return updateAlbum(album.id, {
    is_public: !album.is_public,
  });
};

export const toggleAlbumHome = async (album) => {
  if (!album?.id) throw new Error("toggleAlbumHome: album manquant.");

  return updateAlbum(album.id, {
    show_on_home: !album.show_on_home,
  });
};

export const toggleAlbumItemVisible = async (albumId, item) => {
  if (!albumId) throw new Error("toggleAlbumItemVisible: albumId manquant.");
  if (!item?.id) throw new Error("toggleAlbumItemVisible: item manquant.");

  return updateAlbumItem(albumId, item.id, {
    is_visible: !item.is_visible,
  });
};